/**
 * Gestión de operadores del panel (admin_users): crear, editar rol, activar/desactivar.
 *
 * Roles simplificados expuestos al usuario:
 *   - "admin" (= hm_admin · puede hacer todo excepto crear otro super_admin)
 *   - "viewer" (solo visibilidad)
 *
 * Internamente el sistema también tiene super_admin (el bootstrap),
 * campaign_operator y community_analyst, pero esta UI solo expone los 2 simples.
 */
import { useState } from "react";
import api from "../lib/api";
import PageHeader, { PageBody, Card, Badge, useFetch } from "../components/Shared";
import { toast } from "sonner";
import { UserPlus, ShieldCheck, Eye, Trash, PencilSimple, Key } from "@phosphor-icons/react";

const ROLE_LABEL = {
  super_admin: "Super admin",
  hm_admin: "Admin",
  campaign_operator: "Operador",
  community_analyst: "Analista",
  viewer: "Solo lectura",
};
const ROLE_TONE = {
  super_admin: "terracotta",
  hm_admin: "terracotta",
  campaign_operator: "warning",
  community_analyst: "forest",
  viewer: "neutral",
};

export default function TeamUsers() {
  const { data, loading, reload } = useFetch(() => api.get("/auth/admin-users").then((r) => r.data), []);
  const meQ = useFetch(() => api.get("/auth/me").then((r) => r.data), []);
  const [showCreate, setShowCreate] = useState(false);
  const [edit, setEdit] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [newPass, setNewPass] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const users = data || [];
  const me = meQ.data;

  const doReset = async () => {
    if (!newPass || newPass.length < 8) { toast.error("La contraseña debe tener al menos 8 caracteres"); return; }
    setResetBusy(true);
    try {
      await api.patch(`/auth/admin-users/${resetUser.id}`, { password: newPass });
      toast.success(`Contraseña actualizada para ${resetUser.full_name || resetUser.email}`);
      setResetUser(null); setNewPass("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al resetear"); } finally { setResetBusy(false); }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/auth/admin-users/${u.id}`, { active: !u.active });
      toast.success(u.active ? "Usuario desactivado" : "Usuario activado");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const remove = async (u) => {
    if (!window.confirm(`¿Eliminar a ${u.full_name || u.email}? No se puede deshacer.`)) return;
    try {
      await api.delete(`/auth/admin-users/${u.id}`);
      toast.success("Usuario eliminado"); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <>
      <PageHeader
        label="Sistema · Operadores del panel"
        title="Equipo · Acceso al panel"
        description="Crea operadores para tu equipo. Admin hace todo (gestionar leads, mandar dinámicas, crear templates, configurar números). Solo lectura solo ve los dashboards."
      >
        <button onClick={() => setShowCreate(true)} data-testid="create-team-user-btn"
          className="bg-terracotta text-white text-sm px-3 py-2 rounded flex items-center gap-2 hover:bg-terracotta-hover">
          <UserPlus size={14} /> Crear operador
        </button>
      </PageHeader>
      <PageBody>
        {loading ? <div className="text-carbon-500">Cargando...</div> : (
          <Card className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="team-users-table">
              <thead>
                <tr className="text-left border-b border-carbon-200 text-[11px] uppercase tracking-wide text-carbon-500">
                  <th className="p-3">Nombre</th>
                  <th className="p-3">Email</th>
                  <th className="p-3">Rol</th>
                  <th className="p-3">Estado</th>
                  <th className="p-3">Creado</th>
                  <th className="p-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-carbon-100 hover:bg-page/50" data-testid={`team-user-row-${u.id}`}>
                    <td className="p-3 font-medium">{u.full_name || "—"}{me && u.id === me.id && <span className="ml-2 text-[10px] text-terracotta">(tú)</span>}</td>
                    <td className="p-3 font-mono text-xs text-carbon-600">{u.email}</td>
                    <td className="p-3">
                      <Badge tone={ROLE_TONE[u.role] || "neutral"}>
                        {u.role === "viewer" ? <Eye size={10} className="inline mr-1" /> : <ShieldCheck size={10} className="inline mr-1" />}
                        {ROLE_LABEL[u.role] || u.role}
                      </Badge>
                    </td>
                    <td className="p-3">
                      <Badge tone={u.active ? "forest" : "signal"}>{u.active ? "Activo" : "Desactivado"}</Badge>
                    </td>
                    <td className="p-3 text-carbon-500 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button onClick={() => setEdit(u)} data-testid={`edit-team-${u.id}`}
                          className="text-carbon-500 hover:text-terracotta p-1.5" title="Editar rol">
                          <PencilSimple size={14} />
                        </button>
                        <button onClick={() => { setResetUser(u); setNewPass(""); }} data-testid={`reset-pass-${u.id}`}
                          className="text-carbon-500 hover:text-terracotta p-1.5" title="Resetear contraseña">
                          <Key size={14} />
                        </button>
                        {(!me || u.id !== me.id) && (
                          <button onClick={() => toggleActive(u)} className="text-xs text-carbon-500 hover:text-carbon-900 px-2"
                            data-testid={`toggle-team-${u.id}`}>
                            {u.active ? "Desactivar" : "Activar"}
                          </button>
                        )}
                        {(!me || u.id !== me.id) && (
                          <button onClick={() => remove(u)} className="text-signal hover:bg-signal/10 p-1.5 rounded" title="Eliminar"
                            data-testid={`remove-team-${u.id}`}>
                            <Trash size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-8 text-carbon-400">Sin operadores aún.</td></tr>
                )}
              </tbody>
            </table>
          </Card>
        )}
      </PageBody>
      {(showCreate || edit) && (
        <UserForm
          initial={edit}
          onClose={() => { setShowCreate(false); setEdit(null); }}
          onSaved={() => { setShowCreate(false); setEdit(null); reload(); }}
        />
      )}
      {resetUser && (
        <div className="fixed inset-0 bg-carbon-900/60 z-50 flex items-center justify-center p-4">
          <div className="bg-surface border border-carbon-200 rounded-lg w-full max-w-md p-6" data-testid="reset-pass-modal">
            <div className="section-label mb-1">Seguridad</div>
            <h2 className="font-heading text-xl mb-1">Resetear contraseña</h2>
            <p className="text-sm text-carbon-500 mb-4">
              Nueva contraseña para <b>{resetUser.full_name || resetUser.email}</b>. El usuario deberá usar esta contraseña en su próximo inicio de sesión.
            </p>
            <label className="block text-sm mb-4">
              <span className="text-[11px] uppercase tracking-wide text-carbon-500">Nueva contraseña *</span>
              <input type="password" minLength={8} value={newPass} onChange={(e) => setNewPass(e.target.value)}
                data-testid="reset-pass-input" placeholder="Mínimo 8 caracteres"
                className="w-full bg-page border border-carbon-200 rounded px-3 py-2 mt-1 text-sm font-mono" />
            </label>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => { setResetUser(null); setNewPass(""); }}
                className="px-4 py-2 rounded text-sm text-carbon-600 hover:bg-page">Cancelar</button>
              <button onClick={doReset} disabled={resetBusy} data-testid="reset-pass-confirm"
                className="bg-terracotta text-white px-4 py-2 rounded text-sm flex items-center gap-2 hover:bg-terracotta-hover disabled:opacity-60">
                <Key size={14} /> {resetBusy ? "Guardando..." : "Resetear contraseña"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function UserForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    email: initial?.email || "",
    full_name: initial?.full_name || "",
    role: initial?.role || "hm_admin",
    password: "",
  });
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/auth/admin-users/${initial.id}`, {
          full_name: form.full_name, role: form.role,
        });
        toast.success("Usuario actualizado");
      } else {
        if (!form.password || form.password.length < 8) {
          toast.error("Password debe tener al menos 8 caracteres"); setBusy(false); return;
        }
        await api.post("/auth/admin-users", {
          email: form.email.toLowerCase().trim(),
          full_name: form.full_name.trim(),
          role: form.role,
          password: form.password,
        });
        toast.success("Operador creado");
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al guardar"); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-carbon-900/60 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-surface border border-carbon-200 rounded-lg w-full max-w-md p-6" data-testid="team-user-form">
        <div className="section-label mb-1">Operador del panel</div>
        <h2 className="font-heading text-xl mb-4">{isEdit ? "Editar operador" : "Crear operador"}</h2>

        <label className="block text-sm mb-3">
          <span className="text-[11px] uppercase tracking-wide text-carbon-500">Nombre completo *</span>
          <input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            data-testid="team-fullname-input"
            className="w-full bg-page border border-carbon-200 rounded px-3 py-2 mt-1 text-sm" />
        </label>

        <label className="block text-sm mb-3">
          <span className="text-[11px] uppercase tracking-wide text-carbon-500">Email *</span>
          <input required type="email" value={form.email} disabled={isEdit}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="team-email-input"
            className="w-full bg-page border border-carbon-200 rounded px-3 py-2 mt-1 text-sm font-mono disabled:opacity-60" />
        </label>

        <label className="block text-sm mb-3">
          <span className="text-[11px] uppercase tracking-wide text-carbon-500">Rol</span>
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
            data-testid="team-role-input"
            className="w-full bg-page border border-carbon-200 rounded px-3 py-2 mt-1 text-sm">
            <option value="hm_admin">Admin · puede hacer todo</option>
            <option value="community_analyst">Supervisor · Inteligencia Conversacional</option>
            <option value="campaign_operator">Agente humano · Bandeja Humana + operación</option>
            <option value="viewer">Solo lectura · solo ve dashboards</option>
          </select>
          <div className="text-[10px] text-carbon-500 mt-1">
            {{
              hm_admin: "Puede gestionar leads, mandar dinámicas, crear templates, configurar números, editar la configuración del sistema y aprobar/rechazar recomendaciones y rollbacks del Supervisor.",
              community_analyst: "Ve el módulo de Inteligencia Conversacional (dashboard, diagnósticos, recomendaciones, versiones, escalamientos) y la Bandeja Humana. Corre análisis y diagnósticos, pero NO aprueba cambios (eso es de Admin).",
              campaign_operator: "Atiende la Bandeja Humana (tomar, responder por WhatsApp, resolver, devolver a la IA, copiloto) y opera campañas/leads. No ve el resto del Supervisor ni la configuración del sistema.",
              viewer: "Solo puede entrar al panel y ver los dashboards, listas de leads, conversaciones e inteligencia. No puede crear, editar ni mandar nada.",
            }[form.role] || ""}
          </div>
        </label>

        {!isEdit && (
          <label className="block text-sm mb-4">
            <span className="text-[11px] uppercase tracking-wide text-carbon-500">Password inicial *</span>
            <input required type="password" minLength={8}
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="team-password-input"
              placeholder="Mínimo 8 caracteres"
              className="w-full bg-page border border-carbon-200 rounded px-3 py-2 mt-1 text-sm font-mono" />
            <div className="text-[10px] text-carbon-500 mt-1">Un admin puede resetearla luego desde el ícono de llave en la lista de operadores.</div>
          </label>
        )}

        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="text-sm px-4 py-2 text-carbon-500 hover:text-carbon-700">Cancelar</button>
          <button type="submit" disabled={busy} data-testid="team-save-btn"
            className="text-sm bg-terracotta text-white px-4 py-2 rounded hover:bg-terracotta-hover disabled:opacity-60">
            {busy ? "Guardando..." : (isEdit ? "Guardar cambios" : "Crear operador")}
          </button>
        </div>
      </form>
    </div>
  );
}
